import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { EN } from "./en";
import { ES } from "./es";
import { useAuth } from "../auth/AuthContext";

const DICTS = { en: EN, es: ES };

/**
 * i18n leve PT/EN/ES.
 * - As strings PT do código são as chaves do dicionário (ver ./en.js e ./es.js).
 * - `t(key, vars)` traduz quando lang === "en"|"es"; em PT retorna a própria chave.
 * - Persistência: localStorage `cm_lang` + campo `language` no perfil do
 *   usuário (aplicado automaticamente após o login).
 */
const I18nCtx = createContext({ lang: "pt", setLang: () => {}, t: (s) => s });

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem("cm_lang");
      return saved === "en" || saved === "pt" || saved === "es" ? saved : "pt";
    } catch {
      return "pt";
    }
  });

  // A preferência salva no perfil do usuário vence a escolha local.
  const userLanguage = user && user.language;
  useEffect(() => {
    if (userLanguage === "en" || userLanguage === "pt" || userLanguage === "es") {
      setLangState(userLanguage);
      try {
        localStorage.setItem("cm_lang", userLanguage);
      } catch {
        // ignore storage errors
      }
    }
  }, [userLanguage]);

  useEffect(() => {
    document.documentElement.lang =
      lang === "en" ? "en-US" : lang === "es" ? "es" : "pt-BR";
  }, [lang]);

  const setLang = useCallback((l) => {
    setLangState(l);
    try {
      localStorage.setItem("cm_lang", l);
    } catch {
      // ignore storage errors
    }
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = DICTS[lang];
      let s = dict ? dict[key] ?? key : key;
      if (vars) {
        for (const k of Object.keys(vars)) {
          s = s.split(`{${k}}`).join(String(vars[k]));
        }
      }
      return s;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  return useContext(I18nCtx);
}
